/* eslint-disable indent */
import axios from 'axios'
import { useEffect, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Alert from 'react-bootstrap/Alert'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { useDispatch, useSelector } from 'react-redux'
import { initializeUser } from '../reducers/userReducer'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { getTranslation } from '../services/translations'
import {
  parseAgeStr,
  mapStartToBackendSkills,
  getDefaultStartForPosition,
  formatAge,
  getRequiredSkillsForPosition,
  getForcedSkillsForPosition,
  getMaxSkillsForPosition,
  buildMultipliersPayload,
  buildAddableOptions,
  getPositionDisplayLabel,
  DEFAULT_TRAINING_PARAMS,
  DEFAULT_CAPS,
  formatPercentDisplay,
  parsePercentToDecimal,
  migrateCaps,
} from '../services/skillDistributionUtils'

const DEFAULT_ACTIVE_POSITIONS = [
  'GK',
  'GKD',
  'CD',
  'RWBN',
  'RWTM',
  'RWN',
  'IMN',
  'IMD',
  'IMO',
  'FW',
  'RFTW',
]

const getCurrentSettingsFromDistribution = distribution => {
  if (!distribution || typeof distribution !== 'object') return {}
  if (
    distribution.currentSettings &&
    typeof distribution.currentSettings === 'object'
  ) {
    return distribution.currentSettings
  }
  return distribution
}

const getBasePositionKey = positionKey =>
  String(positionKey || '').split('_')[0] || String(positionKey || '')

const trainingModeLabels = {
  keeperSkill: 'Maalivahti',
  defenderSkill: 'Puolustaminen',
  playmakerSkill: 'Pelinrakennus',
  wingerSkill: 'Laituri',
  passingSkill: 'Syöttäminen',
  scorerSkill: 'Maalinteko',
  setPiecesSkill: 'Erikoistilanteet',
}

const groupTrainingPlan = plan => {
  if (!Array.isArray(plan) || plan.length === 0) return []

  return plan.reduce((groups, entry) => {
    const previous = groups[groups.length - 1]
    if (previous && previous.skill === entry.skill) {
      previous.weeks += 1
      previous.skillAfter = entry.skillAfter
      previous.ageAfter = entry.ageAfter
      return groups
    }

    groups.push({
      skill: entry.skill,
      weeks: 1,
      skillAfter: entry.skillAfter,
      ageAfter: entry.ageAfter,
    })
    return groups
  }, [])
}

const SkillDistribution = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { playerId: playerIdFromUrl } = useParams()
  const user = useSelector(state => state.user)

  // Translation helper
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)

  const [message, setMessage] = useState({ text: '', type: '' })
  const [positions, setPositions] = useState([])
  const [positionTable, setPositionTable] = useState({ header: [], rows: [] })
  const [positionGroup, setPositionGroup] = useState('All')
  const [requiredSkill, setRequiredSkill] = useState('passingSkill')
  const [requiredTarget, setRequiredTarget] = useState(12)
  const [requiredSkills, setRequiredSkills] = useState({})
  const [forcedSkill, setForcedSkill] = useState('passingSkill')
  const [forcedTarget, setForcedTarget] = useState(12)
  const [forcedPositionGroup, setForcedPositionGroup] = useState('All')
  const [forcedSkills, setForcedSkills] = useState({})
  const [maxPositionGroup, setMaxPositionGroup] = useState('All')
  const [maxSkill, setMaxSkill] = useState('passingSkill')
  const [maxTarget, setMaxTarget] = useState(8)
  const [maxSkills, setMaxSkills] = useState({})
  const [activePositions, setActivePositions] = useState(
    DEFAULT_ACTIVE_POSITIONS
  )
  const [fetchedData, setFetchedData] = useState({})
  const [fetchedAges, setFetchedAges] = useState({})
  const [fetchedWeeks, setFetchedWeeks] = useState({})
  const [fetchedPlans, setFetchedPlans] = useState({})
  const [selectedTrainingPlan, setSelectedTrainingPlan] = useState(null)
  const [positionDistributions, setPositionDistributions] = useState({})
  const [customDistributions, setCustomDistributions] = useState({})
  const [startingProfiles, setStartingProfiles] = useState({})
  const [ageCapStr, setAgeCapStr] = useState('28.0')
  const [multipliers, setMultipliers] = useState({ playmakerSkill: 1.5 })
  const defaultTrainingParams = DEFAULT_TRAINING_PARAMS
  const defaultCaps = DEFAULT_CAPS
  const [trainingParams, setTrainingParams] = useState(defaultTrainingParams)
  const [caps, setCaps] = useState(defaultCaps)
  const [showWeeksTable, setShowWeeksTable] = useState(false)
  const [draggedPos, setDraggedPos] = useState(null)
  const [dragOverIdx, setDragOverIdx] = useState(null)
  const [playerIdInput, setPlayerIdInput] = useState('')
  const [selectedPosForFetch, setSelectedPosForFetch] = useState('GK')
  const [positionLabels, setPositionLabels] = useState({})
  const getSelectionLabel = pos => getPositionDisplayLabel(pos, positionLabels)
  const [userToken, setUserToken] = useState(null)
  const [pendingOptimizePlayer, setPendingOptimizePlayer] = useState(null)
  const [isOptimizeMode, setIsOptimizeMode] = useState(false)

  const buildSettingsPayload = () => ({
    activePositions,
    startingProfiles,
    customDistributions,
    positionLabels,
    multipliers,
    ageCapStr,
    requiredSkills,
    forcedSkills,
    fetchedAges,
    fetchedWeeks,
    fetchedPlans,
    maxSkills,
    fetchedData,
    trainingParams,
    caps,
  })

  const applySettingsPayload = payload => {
    const settings = payload && typeof payload === 'object' ? payload : {}
    const savedActivePositions = (
      Array.isArray(settings.activePositions) &&
      settings.activePositions.length > 0
        ? settings.activePositions
        : DEFAULT_ACTIVE_POSITIONS
    ).map(position => (position === 'GK_D' ? 'GKD' : position))
    const nextActivePositions = Array.from(
      new Set([...DEFAULT_ACTIVE_POSITIONS, ...savedActivePositions])
    )

    setActivePositions(nextActivePositions)
    setStartingProfiles(settings.startingProfiles || {})
    setCustomDistributions(settings.customDistributions || {})
    setPositionLabels(settings.positionLabels || {})
    setMultipliers(settings.multipliers || { playmakerSkill: 1.5 })
    setAgeCapStr(settings.ageCapStr || '28.0')
    setRequiredSkills(settings.requiredSkills || {})
    setForcedSkills(settings.forcedSkills || {})
    setFetchedData(settings.fetchedData || {})
    setFetchedAges(settings.fetchedAges || {})
    setFetchedWeeks(settings.fetchedWeeks || {})
    setFetchedPlans(settings.fetchedPlans || {})
    setTrainingParams(settings.trainingParams || defaultTrainingParams)
    setCaps(migrateCaps(settings.caps) || defaultCaps)
    setMaxSkills(settings.maxSkills || {})
    setMaxPositionGroup('All')
    setPendingOptimizePlayer(null)
    setIsOptimizeMode(false)
  }

  const loadSettingsFromDatabase = async () => {
    try {
      if (!userToken) {
        displayMessage(t('userNotRecognized'), 'danger', true)
        return
      }

      const res = await axios.get(`/users/${userToken}`)
      const pd = getCurrentSettingsFromDistribution(
        res.data?.PositionsDistribution
      )

      if (!pd || Object.keys(pd).length === 0) {
        displayMessage(t('noSavedConfiguration'), 'warning', true)
        return
      }

      applySettingsPayload(pd)
      displayMessage(t('savedConfigurationLoaded'), 'info')
    } catch (e) {
      console.error(e)
      displayMessage(t('loadFailed'), 'danger', true)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      const userData = await dispatch(initializeUser())
      if (!userData) return

      const token = userData.oauth_token || userData.id
      setUserToken(token)

      const rawDistribution = userData && userData.PositionsDistribution
      const pd = getCurrentSettingsFromDistribution(rawDistribution)
      if (pd && Object.keys(pd).length > 0) {
        // Load saved settings
        // HUOM: Säilytä olemassa olevat activePositions, älä ylikirjoita niitä
        setActivePositions(prev => {
          // Jos prev on tyhjä tai default, käytä tallennettuja
          if (
            !prev ||
            prev.length === 0 ||
            (prev.length === 10 && prev[0] === 'GK')
          ) {
            return (pd.activePositions || prev).map(position =>
              position === 'GK_D' ? 'GKD' : position
            )
          }
          // Muuten säilytä nykyiset (joihin on jo lisätty pelaajia)
          return prev
        })
        // Yhdistä tallennetut startingProfiles nykyisiin (säilytä juuri lisätyt pelaajat)
        setStartingProfiles(prev => ({
          ...pd.startingProfiles,
          ...prev, // Nykyiset (juuri lisätyt) pelaajat ylikirjoittavat tallennetut
        }))
        // Yhdistä tallennetut customDistributions nykyisiin
        setCustomDistributions(prev => ({
          ...pd.customDistributions,
          ...prev,
        }))
        // Yhdistä tallennetut positionLabels nykyisiin
        setPositionLabels(prev => ({
          ...pd.positionLabels,
          ...prev,
        }))
        setMultipliers(pd.multipliers || { playmakerSkill: 1.5 })
        setAgeCapStr(pd.ageCapStr || '28.0')
        setRequiredSkills(pd.requiredSkills || {})
        setForcedSkills(pd.forcedSkills || {})
        setFetchedData(pd.fetchedData || {})
        setFetchedAges(pd.fetchedAges || {})
        setFetchedWeeks(pd.fetchedWeeks || {})
        setTrainingParams(pd.trainingParams || defaultTrainingParams)
        setCaps(migrateCaps(pd.caps) || defaultCaps)
        setMaxSkills(pd.maxSkills || {})
        setMaxPositionGroup('All')
      }
    }
    loadData()
  }, [])

  // Käsittele pelaajan tietojen vastaanotto navigaatiosta
  useEffect(() => {
    if (location.state && location.state.optimizePlayer) {
      const playerData = location.state.optimizePlayer

      // Tyhjennä navigaation state välittömästi
      window.history.replaceState({}, document.title)

      // Jos positionDistributions on jo ladattu, lisää pelaaja heti
      if (
        positionDistributions &&
        Object.keys(positionDistributions).length > 0
      ) {
        addPlayerFromData(playerData)
      } else {
        // Muuten tallenna pending
        setPendingOptimizePlayer(playerData)
      }
    }
  }, [location.state, positionDistributions])

  // Käsittele pelaajan haku URL-parametrin perusteella
  // HUOM: Tämä ajaa vain jos location.state EI sisällä optimizePlayeria
  // (jos sisältää, käytetään sitä dataa suoraan eikä haeta backendistä)
  useEffect(() => {
    const fetchPlayerFromUrl = async () => {
      // Älä hae jos location.state sisältää jo pelaajan datan
      if (location.state && location.state.optimizePlayer) {
        return
      }

      if (
        playerIdFromUrl &&
        userToken &&
        positionDistributions &&
        Object.keys(positionDistributions).length > 0 &&
        !isOptimizeMode // Älä hae uudelleen jos ollaan jo optimointi-moodissa
      ) {
        try {
          const response = await axios.get(
            `/api/players/getoneplayer/${playerIdFromUrl}/${userToken}`
          )
          if (response.status === 200 && response.data) {
            const player = response.data
            // Käytä pelaajan ensisijaista paikkaa tai oletuspaikkaa
            const basePos = player.position || 'CD'

            const playerData = {
              playerId: player.playerId,
              firstName: player.firstName,
              lastName: player.lastName,
              age: player.age,
              ageDays: player.ageDays,
              playerSkills: player.playerSkills,
              position: basePos,
            }
            addPlayerFromData(playerData)
          }
        } catch (error) {
          console.error('Error fetching player from URL:', error)
          displayMessage(t('noPlayersFound'), 'danger', true)
        }
      }
    }
    fetchPlayerFromUrl()
  }, [
    playerIdFromUrl,
    userToken,
    positionDistributions,
    isOptimizeMode,
    location.state,
  ])

  // Lisää pending pelaaja kun positionDistributions on valmis
  useEffect(() => {
    if (
      pendingOptimizePlayer &&
      positionDistributions &&
      Object.keys(positionDistributions).length > 0
    ) {
      addPlayerFromData(pendingOptimizePlayer)
      setPendingOptimizePlayer(null)
    }
  }, [pendingOptimizePlayer, positionDistributions])

  // Apufunktio pelaajan lisäämiseen
  const addPlayerFromData = playerData => {
    // Aseta optimointi-moodi päälle
    setIsOptimizeMode(true)

    // Lisää pelaaja taulukkoon
    // Varmista että pelipaikka on kelpo positionDistributionissa
    let basePos = playerData.position

    // Tarkista onko annettu pelipaikka olemassa positionDistributionissa
    // Jos ei, kokeile vaihtoehtoisia muotoja (esim. WBN -> RWBN, WTM -> RWTM)
    if (!positionDistributions[basePos]) {
      // Kokeile R-etuliitteellä
      if (positionDistributions['R' + basePos]) {
        basePos = 'R' + basePos
      }
      // Kokeile L-etuliitteellä
      else if (positionDistributions['L' + basePos]) {
        basePos = 'L' + basePos
      }
      // Tarkista onko se muodossa joka pitää muuttaa (esim. pelaajalta tullut muoto)
      else {
        // Etsi vastaava pelipaikka positionDistributionista
        const posKeys = Object.keys(positionDistributions)
        const matchingPos = posKeys.find(p => {
          // Poista R/L etuliite ja vertaa
          const stripped =
            p.startsWith('R') || p.startsWith('L') ? p.slice(1) : p
          const inputStripped =
            basePos.startsWith('R') || basePos.startsWith('L')
              ? basePos.slice(1)
              : basePos
          return stripped === inputStripped
        })
        if (matchingPos) {
          basePos = matchingPos
        }
      }
    }

    const uniquePos = `${basePos}_${playerData.playerId}`

    // Map player skills to our format (kolmen desimaalin tarkkuus)
    const skills = {
      GK: Number((playerData.playerSkills.keeperSkill || 0).toFixed(3)),
      D: Number((playerData.playerSkills.defenderSkill || 0).toFixed(3)),
      PM: Number((playerData.playerSkills.playmakerSkill || 0).toFixed(3)),
      W: Number((playerData.playerSkills.wingerSkill || 0).toFixed(3)),
      PS: Number((playerData.playerSkills.passingSkill || 0).toFixed(3)),
      SC: Number((playerData.playerSkills.scorerSkill || 0).toFixed(3)),
      SP: Number((playerData.playerSkills.setPiecesSkill || 0).toFixed(3)),
    }
    const ageStr = `${playerData.age}.${playerData.ageDays || 0}`

    // Tyhjennä activePositions ja aseta vain tämä pelaaja
    setActivePositions([uniquePos])

    // Nollaa minimitaidot, maksimitaidot ja lukitut taidot
    setRequiredSkills({})
    setMaxSkills({})
    setForcedSkills({})

    // Tyhjennä aiemmat haetut tulokset
    setFetchedData({})
    setFetchedAges({})
    setFetchedWeeks({})

    // Set starting profile for the new position
    setStartingProfiles({
      [uniquePos]: { skills, ageStr },
    })

    // Create label: position + last name (näytä ilman R/L etuliitettä)
    const displayPos =
      basePos.startsWith('R') || basePos.startsWith('L')
        ? basePos.slice(1)
        : basePos
    const lastName = playerData.lastName || ''
    setPositionLabels({
      [uniquePos]: `${displayPos} ${lastName}`,
    })

    // Set custom distribution for this position using base position's distribution
    setCustomDistributions(prev => ({
      [uniquePos]: prev[basePos] || positionDistributions[basePos],
    }))

    displayMessage(`${playerData.lastName} ${t('playersAdded')}`, 'success')
  }

  // Funktio pelipaikan vaihtamiseen pelaajalle
  const handlePositionChange = (oldPos, newBasePos) => {
    // Tarkista että kyseessä on pelaaja (sisältää ID:n)
    const parts = oldPos.split('_')
    if (parts.length < 2) return

    const playerId = parts[1]
    const newPos = `${newBasePos}_${playerId}`

    // Päivitä activePositions
    setActivePositions(prev => prev.map(p => (p === oldPos ? newPos : p)))

    // Päivitä startingProfiles
    setStartingProfiles(prev => {
      const newProfiles = { ...prev }
      if (newProfiles[oldPos]) {
        newProfiles[newPos] = newProfiles[oldPos]
        delete newProfiles[oldPos]
      }
      return newProfiles
    })

    // Päivitä positionLabels (vaihda pelipaikka labelissa)
    setPositionLabels(prev => {
      const newLabels = { ...prev }
      if (newLabels[oldPos]) {
        // Erota sukunimi vanhasta labelista
        const oldLabel = newLabels[oldPos]
        const labelParts = oldLabel.split(' ')
        const lastName = labelParts.slice(1).join(' ') // Kaikki paitsi ensimmäinen osa
        newLabels[newPos] = `${newBasePos} ${lastName}`
        delete newLabels[oldPos]
      }
      return newLabels
    })

    // Päivitä customDistributions (hae uuden pelipaikan distribution)
    setCustomDistributions(prev => {
      const newDists = { ...prev }
      newDists[newPos] = positionDistributions[newBasePos] || prev[newBasePos]
      delete newDists[oldPos]
      return newDists
    })

    // Päivitä fetchedData, fetchedAges, fetchedWeeks (tyhjennä vanhat tulokset)
    setFetchedData(prev => {
      const newData = { ...prev }
      delete newData[oldPos]
      return newData
    })
    setFetchedAges(prev => {
      const newAges = { ...prev }
      delete newAges[oldPos]
      return newAges
    })
    setFetchedWeeks(prev => {
      const newWeeks = { ...prev }
      delete newWeeks[oldPos]
      return newWeeks
    })
    setFetchedPlans(prev => {
      const newPlans = { ...prev }
      delete newPlans[oldPos]
      return newPlans
    })
  }

  const copyAsNewPlayer = sourcePos => {
    const sourceSkillValues = fetchedData[sourcePos]
    const sourceAge = fetchedAges[sourcePos]
    const sourceProfile = startingProfiles[sourcePos]
    if (
      !Array.isArray(sourceSkillValues) ||
      sourceSkillValues.length !== 7 ||
      !Array.isArray(sourceAge) ||
      !sourceProfile
    ) {
      displayMessage('Aja optimointi ennen pelaajan kopiointia', 'warning', true)
      return
    }

    const skillKeys = ['GK', 'D', 'PM', 'W', 'PS', 'SC', 'SP']
    const normalizeSkill = value => {
      const numericValue = Number(String(value).replace(',', '.'))
      return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value
    }
    const sourceSkills = Object.fromEntries(
      Object.entries(sourceProfile.skills).map(([skill, value]) => [
        skill,
        normalizeSkill(value),
      ])
    )
    sourceSkillValues.forEach((value, index) => {
      const achievedLevel = Number(String(value).replace(',', '.'))
      if (Number.isFinite(achievedLevel)) {
        sourceSkills[skillKeys[index]] = normalizeSkill(value)
      }
    })

    const basePos = getBasePositionKey(sourcePos)
    const copiedPos = `${basePos}_copy${Date.now()}`
    setActivePositions(prev => [...prev, copiedPos])
    setStartingProfiles(prev => ({
      ...prev,
      [copiedPos]: {
        skills: sourceSkills,
        ageStr: formatAge(sourceAge),
      },
    }))
    setPositionLabels(prev => ({
      ...prev,
      [copiedPos]: `${basePos} Kopio`,
    }))
    setCustomDistributions(prev => ({
      ...prev,
      [copiedPos]: prev[sourcePos] || positionDistributions[basePos],
    }))
  }

  useEffect(() => {
    // Haetaan pelipaikat backendistä
    axios
      .get('/api/players/positions')
      .then(response => {
        setPositions(Object.keys(response.data))
        setPositionDistributions(response.data)
        setCustomDistributions(response.data)
      })
      .catch(error => console.error('Error fetching positions:', error))

    // Alusta taulukko otsikolla
    const skillLabels = ['GK', 'D', 'PM', 'W', 'PS', 'SC', 'SP']
    setPositionTable({
      header: [' ', ...skillLabels, 'Age', 'Poista', 'Kopioi', 'Vie'],
      rows: [],
    })
  }, [])

  // Ensure starting profiles exist for all active positions
  useEffect(() => {
    setStartingProfiles(prev => {
      const next = { ...prev }
      for (const pos of activePositions) {
        if (!next[pos]) {
          next[pos] = {
            skills: getDefaultStartForPosition(pos),
            ageStr: '17.0',
          }
        }
      }
      return next
    })
  }, [activePositions])

  const handleDragStart = (e, pos) => {
    setDraggedPos(pos)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIdx(idx)
  }

  const handleDragLeave = () => {
    setDragOverIdx(null)
  }

  const handleDrop = (e, idx) => {
    e.preventDefault()
    if (draggedPos === null) return

    const draggedIdx = activePositions.indexOf(draggedPos)
    if (draggedIdx === idx) {
      setDraggedPos(null)
      setDragOverIdx(null)
      return
    }

    const newPositions = [...activePositions]
    newPositions.splice(draggedIdx, 1)
    newPositions.splice(idx, 0, draggedPos)
    setActivePositions(newPositions)
    setDraggedPos(null)
    setDragOverIdx(null)
  }

  const handleDragEnd = () => {
    setDraggedPos(null)
    setDragOverIdx(null)
  }

  // Run optimizer for a single position (used when adding a position)
  const runOptimizationForPosition = async position => {
    try {
      // Extract base position from unique ID (e.g., "GK_1234567890" -> "GK")
      const basePosition = position.split('_')[0]
      const optimizationPosition =
        basePosition === 'GKD' ? 'GK' : basePosition

      // Validate distribution for the specific position
      const validatedDist = JSON.parse(JSON.stringify(customDistributions))
      if (validatedDist[position]) {
        for (const bucket of Object.keys(validatedDist[position])) {
          for (const skill of Object.keys(validatedDist[position][bucket])) {
            let value = validatedDist[position][bucket][skill]
            if (typeof value === 'string') {
              value = value.replace(',', '.')
              const numValue = parseFloat(value)
              if (isNaN(numValue)) {
                displayMessage(
                  `Virheellinen arvo ${position}/${bucket}/${skill}: "${value}"`,
                  'danger',
                  true
                )
                return
              }
              validatedDist[position][bucket][skill] = numValue
            }
          }
        }
      }

      const filteredRequired = getRequiredSkillsForPosition(
        position,
        requiredSkills
      )
      const filteredForced = getForcedSkillsForPosition(position, forcedSkills)
      const filteredMax = getMaxSkillsForPosition(position, maxSkills)
      const start = startingProfiles[position] || {
        skills: getDefaultStartForPosition(position),
        ageStr: '17.0',
      }
      const currentSkills = mapStartToBackendSkills(start.skills)
      const age = parseAgeStr(start.ageStr)
      const maxAge = parseAgeStr(ageCapStr)
      const mp = buildMultipliersPayload(multipliers)
      const tp = {
        coach: Number(String(trainingParams.coach).replace(',', '.')) || 0,
        assistants:
          Number(String(trainingParams.assistants).replace(',', '.')) || 0,
        intensity:
          Number(String(trainingParams.intensity).replace(',', '.')) || 0,
        stamina: Number(String(trainingParams.stamina).replace(',', '.')) || 0,
        minutes: Number(String(trainingParams.minutes).replace(',', '.')) || 0,
        efficiency:
          Number(String(trainingParams.efficiency).replace(',', '.')) || 0,
      }
      const capsPayload = Object.fromEntries(
        Object.entries(caps).map(([k, v]) => [
          k,
          Number(String(v).replace(',', '.')) || 0,
        ])
      )

      const body = {
        currentSkills,
        age,
        coach: tp.coach,
        assistants: tp.assistants,
        intensity: tp.intensity,
        stamina: tp.stamina,
        minutes: tp.minutes,
        efficiency: tp.efficiency,
        position: optimizationPosition,
        objective: 'combined',
        maxAge,
        multipliers: mp,
        requiredSkills: { ...filteredRequired, ...filteredForced },
        maxSkills: { ...filteredMax, ...filteredForced },
        caps: capsPayload,
        dist:
          validatedDist[position] || positionDistributions[optimizationPosition],
      }

      const skillOrder = [
        'keeperSkill',
        'defenderSkill',
        'playmakerSkill',
        'wingerSkill',
        'passingSkill',
        'scorerSkill',
        'setPiecesSkill',
      ]

      const res = await axios.post('/api/players/optimize-distribution', body)
      if (res.status === 200) {
        const row = skillOrder.map(k =>
          res.data.finalSkills[k] !== undefined &&
          res.data.finalSkills[k] !== null
            ? res.data.finalSkills[k]
            : '-'
        )
        setFetchedData(prev => ({ ...prev, [position]: row }))
        setFetchedAges(prev => ({ ...prev, [position]: res.data.finalAge }))
        const weeksRow = skillOrder.map(k =>
          res.data.weeksPerSkill[k] !== undefined &&
          res.data.weeksPerSkill[k] !== null
            ? res.data.weeksPerSkill[k]
            : '-'
        )
        setFetchedWeeks(prev => ({ ...prev, [position]: weeksRow }))
        setFetchedPlans(prev => ({ ...prev, [position]: res.data.plan || [] }))
      }
    } catch (e) {
      console.error(e)
      displayMessage(t('searchFailed'), 'danger', true)
    }
  }

  const displayMessage = (text, type, persistent = false) => {
    setMessage({ text, type })
    if (!persistent) {
      setTimeout(() => setMessage({ text: '', type: '' }), 2000)
    }
  }

  const fetchPlayerById = async () => {
    if (!playerIdInput || !userToken) {
      displayMessage('Anna pelaajan ID', 'warning', true)
      return
    }

    // Extract all 9-digit numbers from input (supports formats like "(500375416), (500375413)")
    const idMatches = playerIdInput.match(/\d{9}/g)
    if (!idMatches || idMatches.length === 0) {
      displayMessage(t('noValidPlayerIds'), 'warning', true)
      return
    }

    let successCount = 0
    let failCount = 0

    for (const playerId of idMatches) {
      try {
        const response = await axios.get(
          `/api/players/getoneplayer/${playerId}/${userToken}`
        )
        if (response.status === 200 && response.data) {
          const player = response.data
          const basePos = selectedPosForFetch
          // Create unique position identifier using player ID
          const uniquePos = `${basePos}_${playerId}`

          // Map player skills to our format (kolmen desimaalin tarkkuus)
          const skills = {
            GK: Number((player.playerSkills.keeperSkill || 0).toFixed(3)),
            D: Number((player.playerSkills.defenderSkill || 0).toFixed(3)),
            PM: Number((player.playerSkills.playmakerSkill || 0).toFixed(3)),
            W: Number((player.playerSkills.wingerSkill || 0).toFixed(3)),
            PS: Number((player.playerSkills.passingSkill || 0).toFixed(3)),
            SC: Number((player.playerSkills.scorerSkill || 0).toFixed(3)),
            SP: Number((player.playerSkills.setPiecesSkill || 0).toFixed(3)),
          }
          const ageStr = `${player.age}.${player.ageDays || 0}`

          // Add new position to active positions list
          setActivePositions(prev => [...prev, uniquePos])

          // Set starting profile for the new position
          setStartingProfiles(prev => ({
            ...prev,
            [uniquePos]: { skills, ageStr },
          }))

          // Create label: position + full last name (e.g., "GK Järvinen")
          const label = basePos.startsWith('R') ? basePos.slice(1) : basePos
          const lastName = player.lastName || ''
          setPositionLabels(prev => ({
            ...prev,
            [uniquePos]: `${label} ${lastName}`,
          }))

          // Set custom distribution for this position using base position's distribution
          setCustomDistributions(prev => ({
            ...prev,
            [uniquePos]: prev[basePos] || positionDistributions[basePos],
          }))

          successCount++

          // Small delay between requests to avoid overwhelming the server
          if (idMatches.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }
      } catch (error) {
        failCount++
        console.error(`Error fetching player ${playerId}:`, error)
      }
    }

    if (successCount > 0) {
      displayMessage(
        `${successCount} ${t('playersAdded')}${
          failCount > 0 ? `, ${failCount} ${t('playersFailed')}` : ''
        }`,
        successCount > 0 ? 'success' : 'warning'
      )
    } else {
      displayMessage(t('noPlayersFound'), 'danger', true)
    }

    setPlayerIdInput('')
  }

  const testAllPositions = async () => {
    displayMessage('Aloitetaan haku kaikille pelipaikoille...', 'info')
    const newFetchedData = { ...fetchedData }
    const newFetchedAges = { ...fetchedAges }
    const newFetchedWeeks = { ...fetchedWeeks }
    const newFetchedPlans = { ...fetchedPlans }

    const filteredPositions = activePositions.filter(
      pos => !pos.split('_')[0].startsWith('L')
    )
    const skillOrder = [
      'keeperSkill',
      'defenderSkill',
      'playmakerSkill',
      'wingerSkill',
      'passingSkill',
      'scorerSkill',
      'setPiecesSkill',
    ]

    try {
      // Muunna requiredSkills takaisin backend formatiin ja suodata positioiden perusteella

      for (const position of filteredPositions) {
        // Extract base position from unique ID (e.g., "GK_1234567890" -> "GK")
        const basePosition = position.split('_')[0]
        const optimizationPosition =
          basePosition === 'GKD' ? 'GK' : basePosition

        // Validoi ja muokkaa customDistributions arvot
        const validatedDist = JSON.parse(JSON.stringify(customDistributions))
        for (const pos of Object.keys(validatedDist)) {
          for (const bucket of Object.keys(validatedDist[pos])) {
            for (const skill of Object.keys(validatedDist[pos][bucket])) {
              let value = validatedDist[pos][bucket][skill]
              if (typeof value === 'string') {
                // Korvaa pilkut pisteiksi
                value = value.replace(',', '.')
                const numValue = parseFloat(value)
                if (isNaN(numValue)) {
                  displayMessage(
                    `Virheellinen arvo ${pos}/${bucket}/${skill}: "${value}"`,
                    'danger',
                    true
                  )
                  return
                }
                validatedDist[pos][bucket][skill] = numValue
              }
            }
          }
        }

        const filteredRequiredSkills = getRequiredSkillsForPosition(
          position,
          requiredSkills
        )
        const filteredForcedSkills = getForcedSkillsForPosition(
          position,
          forcedSkills
        )
        const filteredMaxSkills = getMaxSkillsForPosition(
          position,
          maxSkills
        )
        // Build current skills and ages from startingProfiles
        const start = startingProfiles[position] || {
          skills: getDefaultStartForPosition(position),
          ageStr: '17.0',
        }
        const currentSkills = mapStartToBackendSkills(start.skills)
        const age = parseAgeStr(start.ageStr)
        const maxAge = parseAgeStr(ageCapStr)
        const tp = {
          coach: Number(String(trainingParams.coach).replace(',', '.')) || 0,
          assistants:
            Number(String(trainingParams.assistants).replace(',', '.')) || 0,
          intensity:
            Number(String(trainingParams.intensity).replace(',', '.')) || 0,
          stamina:
            Number(String(trainingParams.stamina).replace(',', '.')) || 0,
          minutes:
            Number(String(trainingParams.minutes).replace(',', '.')) || 0,
          efficiency:
            Number(String(trainingParams.efficiency).replace(',', '.')) || 0,
        }
        const capsPayload = Object.fromEntries(
          Object.entries(caps).map(([k, v]) => [
            k,
            Number(String(v).replace(',', '.')) || 0,
          ])
        )

        // Build multipliers payload from state
        const mp = buildMultipliersPayload(multipliers)
        const body = {
          currentSkills,
          age,
          coach: tp.coach,
          assistants: tp.assistants,
          intensity: tp.intensity,
          stamina: tp.stamina,
          minutes: tp.minutes,
          efficiency: tp.efficiency,
          position: optimizationPosition,
          objective: 'combined',
          maxAge,
          multipliers: mp,
          requiredSkills: {
            ...filteredRequiredSkills,
            ...filteredForcedSkills,
          },
          maxSkills: { ...filteredMaxSkills, ...filteredForcedSkills },
          caps: capsPayload,
          dist:
            validatedDist[position] ||
            positionDistributions[optimizationPosition],
        }

        const res = await axios.post('/api/players/optimize-distribution', body)
        if (res.status === 200) {
          const row = skillOrder.map(k =>
            res.data.finalSkills[k] !== undefined &&
            res.data.finalSkills[k] !== null
              ? res.data.finalSkills[k]
              : '-'
          )
          newFetchedData[position] = row
          newFetchedAges[position] = res.data.finalAge
          const weeksRow = skillOrder.map(k =>
            res.data.weeksPerSkill[k] !== undefined &&
            res.data.weeksPerSkill[k] !== null
              ? res.data.weeksPerSkill[k]
              : '-'
          )
          newFetchedWeeks[position] = weeksRow
          newFetchedPlans[position] = res.data.plan || []
        }
      }

      setFetchedData(newFetchedData)
      setFetchedAges(newFetchedAges)
      setFetchedWeeks(newFetchedWeeks)
      setFetchedPlans(newFetchedPlans)
      displayMessage(t('searchCompleted'), 'success')
    } catch (e) {
      console.error(e)
      displayMessage(t('searchFailed'), 'danger', true)
    }
  }

  // Funktio vie optimoidut taidot takaisin pelaajan sivulle
  const handleExportPlan = pos => {
    // Etsi alkuperäinen pelaajan ID positionista (esim. "CD_123456789" -> "123456789")
    const parts = pos.split('_')
    if (parts.length < 2) {
      displayMessage('Pelipaikka ei sisällä pelaajan ID:tä', 'warning', true)
      return
    }
    const playerId = parts.slice(1).join('_')

    // Hae optimoidut taidot fetchedData:sta
    const optimizedSkills = fetchedData[pos]
    if (!optimizedSkills) {
      displayMessage(
        'Optimoituja taitoja ei löytynyt. Aja ensin "Hae kaikki".',
        'warning',
        true
      )
      return
    }

    // Muunna taidot oikeaan muotoon
    const targetSkills = [
      { skill: 'keeperSkill', target: optimizedSkills[0] },
      { skill: 'defenderSkill', target: optimizedSkills[1] },
      { skill: 'playmakerSkill', target: optimizedSkills[2] },
      { skill: 'wingerSkill', target: optimizedSkills[3] },
      { skill: 'passingSkill', target: optimizedSkills[4] },
      { skill: 'scorerSkill', target: optimizedSkills[5] },
      { skill: 'setPiecesSkill', target: optimizedSkills[6] },
    ]

    // Navigoi takaisin pelaajan sivulle optimoiduilla taidoilla
    navigate(`/players/${playerId}`, {
      state: {
        exportedTargetSkills: targetSkills,
        optimizedAge: fetchedAges[pos],
        optimizedWeeks: fetchedWeeks[pos],
      },
    })
  }

  const saveSettings = async () => {
    try {
      if (!userToken) {
        displayMessage(t('userNotRecognized'), 'danger', true)
        return
      }
      const payload = buildSettingsPayload()
      const res = await axios.post(
        `/users/positions-distribution/${userToken}`,
        payload
      )
      if (res.status === 200) displayMessage(t('settingsSaved'), 'success')
    } catch (e) {
      console.error(e)
      displayMessage(t('saveFailed'), 'danger', true)
    }
  }

  const resetDefaults = () => {
    const extraPositions = activePositions.filter(
      pos => !DEFAULT_ACTIVE_POSITIONS.includes(pos)
    )
    const resetPositions = [...DEFAULT_ACTIVE_POSITIONS, ...extraPositions]

    // Reset to defaults
    setMultipliers({ playmakerSkill: 1.5 })
    setAgeCapStr('28.0')
    setTrainingParams(defaultTrainingParams)
    setCaps(defaultCaps)
    setMaxSkills({})
    setActivePositions(resetPositions)
    // Rebuild starting profiles for every active position.
    setStartingProfiles(prev => {
      const next = {}
      for (const pos of DEFAULT_ACTIVE_POSITIONS) {
        next[pos] = { skills: getDefaultStartForPosition(pos), ageStr: '17.0' }
      }
      for (const pos of extraPositions) {
        next[pos] = {
          skills: getDefaultStartForPosition(getBasePositionKey(pos)),
          ageStr: '17.0',
        }
      }
      return next
    })
    // Reset distributions to backend defaults, preserve extra positions
    setCustomDistributions(prev => {
      const next = { ...positionDistributions }

      for (const pos of extraPositions) {
        const basePos = getBasePositionKey(pos)
        next[pos] =
          prev[pos] ||
          prev[basePos] ||
          positionDistributions[basePos] ||
          positionDistributions.GK
      }

      return next
    })
    setRequiredSkills({})
    setForcedSkills({})
    setFetchedAges({})
    setFetchedWeeks({})
    displayMessage(t('defaultValuesRestored'), 'info')
  }

  return (
    <Card
      style={{
        backgroundColor: '#2C2F33',
        color: '#FFFFFF',
        borderColor: '#1E2124',
      }}
    >
      <Card.Body>
        <Card.Title>{t('skillDistribution')}</Card.Title>
        <Row>
          <Col md={12}>
            <div className="mb-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tt-required">{t('skillsMayExceed')}</Tooltip>
                }
              >
                <Form.Label style={{ cursor: 'help' }}>
                  {t('minimumSkills')}
                </Form.Label>
              </OverlayTrigger>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={requiredSkill}
                    onChange={e => setRequiredSkill(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="keeperSkill">keeperSkill</option>
                    <option value="defenderSkill">defenderSkill</option>
                    <option value="playmakerSkill">playmakerSkill</option>
                    <option value="wingerSkill">wingerSkill</option>
                    <option value="passingSkill">passingSkill</option>
                    <option value="scorerSkill">scorerSkill</option>
                    <option value="setPiecesSkill">setPiecesSkill</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="number"
                    min="1"
                    max="25"
                    value={requiredTarget}
                    onChange={e => setRequiredTarget(Number(e.target.value))}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={positionGroup}
                    onChange={e => setPositionGroup(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="All">All</option>
                    <option value="Defender">Defender</option>
                    <option value="Inner">Inner</option>
                    <option value="Forward">Forward</option>
                    {/* Single active positions */}
                    {activePositions.map((pos, idx) => {
                      const label = getSelectionLabel(pos)
                      return (
                        <option key={`pos-${idx}`} value={pos}>
                          {label}
                        </option>
                      )
                    })}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const key = `${requiredSkill}_${positionGroup}`
                      setRequiredSkills(prev => ({
                        ...prev,
                        [key]: requiredTarget,
                      }))
                    }}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
              {Object.keys(requiredSkills).length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <small>Pakolliset: </small>
                  <small>
                    {Object.entries(requiredSkills)
                      .map(([k, v]) => {
                        const separatorIndex = k.indexOf('_')
                        const skill = k.slice(0, separatorIndex)
                        const target = k.slice(separatorIndex + 1)
                        const displaySkill = skill.replace('Skill', '')
                        const formatTarget = t => {
                          if (t === 'All') return ''
                          if (['Defender', 'Inner', 'Forward'].includes(t))
                            return t.toLowerCase() + 's '
                          if (t === 'GK') return 'GK '
                          const lbl = getSelectionLabel(t)
                          return lbl + ' '
                        }
                        return `${formatTarget(target)}${displaySkill}→${v}`
                      })
                      .join(', ')}
                  </small>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setRequiredSkills({})}
                    style={{ padding: '0 0 0 8px', color: '#dc3545' }}
                  >
                    {t('clear')}
                  </Button>
                </div>
              )}
            </div>
            <div className="mb-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tt-max">{t('skillsMayNotReach')}</Tooltip>
                }
              >
                <Form.Label style={{ cursor: 'help' }}>
                  {t('maximumSkills')}
                </Form.Label>
              </OverlayTrigger>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={maxSkill}
                    onChange={e => setMaxSkill(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="keeperSkill">keeperSkill</option>
                    <option value="defenderSkill">defenderSkill</option>
                    <option value="playmakerSkill">playmakerSkill</option>
                    <option value="wingerSkill">wingerSkill</option>
                    <option value="passingSkill">passingSkill</option>
                    <option value="scorerSkill">scorerSkill</option>
                    <option value="setPiecesSkill">setPiecesSkill</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="number"
                    min="1"
                    max="25"
                    value={maxTarget}
                    onChange={e => setMaxTarget(Number(e.target.value))}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={maxPositionGroup}
                    onChange={e => setMaxPositionGroup(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="All">All</option>
                    <option value="Defender">Defender</option>
                    <option value="Inner">Inner</option>
                    <option value="Forward">Forward</option>
                    {activePositions.map((pos, idx) => {
                      const label = getSelectionLabel(pos)
                      return (
                        <option key={`max-pos-${idx}`} value={pos}>
                          {label}
                        </option>
                      )
                    })}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const key = `${maxSkill}_${maxPositionGroup}`
                      setMaxSkills(prev => ({
                        ...prev,
                        [key]: maxTarget,
                      }))
                    }}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
              {Object.keys(maxSkills).length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <small>Maksimit: </small>
                  <small>
                    {Object.entries(maxSkills)
                      .map(([k, v]) => {
                        const separatorIndex = k.indexOf('_')
                        const skill = k.slice(0, separatorIndex)
                        const target = k.slice(separatorIndex + 1)
                        const displaySkill = skill.replace('Skill', '')
                        const formatTarget = t => {
                          if (t === 'All') return ''
                          if (['Defender', 'Inner', 'Forward'].includes(t))
                            return t.toLowerCase() + 's '
                          if (t === 'GK') return 'GK '
                          const lbl = getSelectionLabel(t)
                          return lbl + ' '
                        }
                        return `${formatTarget(target)}${displaySkill}<=${v}`
                      })
                      .join(', ')}
                  </small>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setMaxSkills({})}
                    style={{ padding: '0 0 0 8px', color: '#dc3545' }}
                  >
                    {t('clear')}
                  </Button>
                </div>
              )}
            </div>
            <div className="mb-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tt-forced">{t('locksSkillLevel')}</Tooltip>
                }
              >
                <Form.Label style={{ cursor: 'help' }}>
                  Lukittu taitotaso:
                </Form.Label>
              </OverlayTrigger>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Select
                    value={forcedSkill}
                    onChange={e => setForcedSkill(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="keeperSkill">keeperSkill</option>
                    <option value="defenderSkill">defenderSkill</option>
                    <option value="playmakerSkill">playmakerSkill</option>
                    <option value="wingerSkill">wingerSkill</option>
                    <option value="passingSkill">passingSkill</option>
                    <option value="scorerSkill">scorerSkill</option>
                    <option value="setPiecesSkill">setPiecesSkill</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="number"
                    min="1"
                    max="25"
                    value={forcedTarget}
                    onChange={e => setForcedTarget(Number(e.target.value))}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  />
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={forcedPositionGroup}
                    onChange={e => setForcedPositionGroup(e.target.value)}
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                    }}
                  >
                    <option value="All">All</option>
                    <option value="Defender">Defender</option>
                    <option value="Inner">Inner</option>
                    <option value="Forward">Forward</option>
                    {activePositions.map((pos, idx) => {
                      const label = getSelectionLabel(pos)
                      return (
                        <option key={`forced-pos-${idx}`} value={pos}>
                          {label}
                        </option>
                      )
                    })}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const key = `${forcedSkill}_${forcedPositionGroup}`
                      setForcedSkills(prev => ({
                        ...prev,
                        [key]: forcedTarget,
                      }))
                    }}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
              {Object.keys(forcedSkills).length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <small>Pakotetut: </small>
                  <small>
                    {Object.entries(forcedSkills)
                      .map(([k, v]) => {
                        const separatorIndex = k.indexOf('_')
                        const skill = k.slice(0, separatorIndex)
                        const target = k.slice(separatorIndex + 1)
                        const displaySkill = skill.replace('Skill', '')
                        const formatTarget = t => {
                          if (t === 'All') return ''
                          if (['Defender', 'Inner', 'Forward'].includes(t))
                            return t.toLowerCase() + 's '
                          if (t === 'GK') return 'GK '
                          const lbl = getSelectionLabel(t)
                          return lbl + ' '
                        }
                        return `${formatTarget(target)}${displaySkill}=${v}`
                      })
                      .join(', ')}
                  </small>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setForcedSkills({})}
                    style={{ padding: '0 0 0 8px', color: '#dc3545' }}
                  >
                    {t('clear')}
                  </Button>
                </div>
              )}
            </div>
            <div className="mb-2">
              <Form.Label className="me-2">Age cap (y.d)</Form.Label>
              <Form.Control
                type="text"
                value={ageCapStr}
                onChange={e => setAgeCapStr(e.target.value)}
                style={{
                  display: 'inline-block',
                  width: '120px',
                  backgroundColor: '#23272A',
                  color: '#FFFFFF',
                  border: '1px solid #1E2124',
                }}
              />
            </div>
            <div className="d-flex align-items-end mb-2">
              {/* Piilotetaan pelipaikan lisääminen kun ollaan optimointi-moodissa */}
              {!isOptimizeMode && (
                <div style={{ display: 'inline-block', marginRight: '12px' }}>
                  <Form.Label>{t('addPositionToTable')}</Form.Label>
                  <Form.Select
                    style={{
                      backgroundColor: '#23272A',
                      color: '#FFFFFF',
                      border: '1px solid #1E2124',
                      marginTop: '4px',
                    }}
                    onChange={e => {
                      const pos = e.target.value
                      if (pos) {
                        // Create unique position identifier
                        const uniquePos = `${pos}_${Date.now()}_${Math.random()
                          .toString(36)
                          .substr(2, 5)}`

                        setActivePositions(prev => [...prev, uniquePos])
                        // Initialize defaults if missing
                        setStartingProfiles(prev => ({
                          ...prev,
                          [uniquePos]: {
                            skills: getDefaultStartForPosition(pos),
                            ageStr: '17.0',
                          },
                        }))
                        // Set custom distribution for this position
                        setCustomDistributions(prev => ({
                          ...prev,
                          [uniquePos]: prev[pos] || positionDistributions[pos],
                        }))
                        // Trigger fetch for the newly added position
                        runOptimizationForPosition(uniquePos)
                      }
                      e.target.value = ''
                    }}
                  >
                    <option value="">{t('selectPosition')}</option>
                    {buildAddableOptions(positions).map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
              <div className="ms-auto d-flex gap-2 align-items-center">
                {message.text && (
                  <Alert
                    variant={message.type}
                    className="mb-0 py-2 px-3"
                    style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                  >
                    {message.text}
                  </Alert>
                )}
                <Button variant="secondary" onClick={resetDefaults}>
                  {t('reset')}
                </Button>
                <Button
                  variant="outline-info"
                  onClick={loadSettingsFromDatabase}
                >
                  {t('loadSave')}
                </Button>
                {!isOptimizeMode && (
                  <Button variant="primary" onClick={saveSettings}>
                    {t('save')}
                  </Button>
                )}
                <Button variant="success" onClick={testAllPositions}>
                  {t('fetchAll')}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Card
              style={{
                backgroundColor: '#23272A',
                borderColor: '#1E2124',
              }}
            >
              <Card.Body>
                <Card.Title>Aktiiviset pelipaikat</Card.Title>
                <Table striped bordered hover variant="dark" size="sm">
                  <thead>
                    <tr>
                      {positionTable.header.map(col => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                    <tbody>
                    {activePositions.map((pos, idx) => {
                      // Use custom label if it exists, otherwise format the position.
                      const basePos = pos.split('_')[0]
                      const label = getPositionDisplayLabel(pos, positionLabels)
                      const data = fetchedData[pos] || Array(7).fill('-')
                      const ageVal = formatAge(fetchedAges[pos])
                      const isDragOver = dragOverIdx === idx
                      const isDragged = draggedPos === pos
                      return (
                        <tr
                          key={`active-${idx}`}
                          draggable
                          onDragStart={e => handleDragStart(e, pos)}
                          onDragOver={e => handleDragOver(e, idx)}
                          onDragLeave={handleDragLeave}
                          onDrop={e => handleDrop(e, idx)}
                          onDragEnd={handleDragEnd}
                          style={{
                            opacity: isDragged ? 0.5 : 1,
                            backgroundColor: isDragOver
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'transparent',
                            cursor: 'grab',
                          }}
                        >
                          <td style={{ minWidth: '80px', maxWidth: '150px' }}>
                            {/* Näytä vetovalikko vain jos position sisältää pelaajan ID:n */}
                            {pos.includes('_') ? (
                              <Form.Select
                                size="sm"
                                value={basePos}
                                onChange={e =>
                                  handlePositionChange(pos, e.target.value)
                                }
                                style={{
                                  backgroundColor: '#23272A',
                                  color: '#FFFFFF',
                                  border: '1px solid #1E2124',
                                  fontSize: '0.85rem',
                                  padding: '2px 4px',
                                }}
                              >
                                {buildAddableOptions(positions).map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}{' '}
                                    {positionLabels[pos]
                                      ? positionLabels[pos]
                                          .split(' ')
                                          .slice(1)
                                          .join(' ')
                                      : ''}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              label
                            )}
                          </td>
                          {data.map((val, i) => (
                            <td
                              key={`skill-${idx}-${i}`}
                              style={{ width: '100px' }}
                            >
                              {typeof val === 'number' ? val.toFixed(2) : val}
                            </td>
                          ))}
                          <td style={{ width: '100px' }}>{ageVal}</td>
                          <td style={{ width: '100px' }}>
                            <Button
                              variant="link"
                              size="sm"
                              style={{ color: '#dc3545', padding: '0' }}
                              onClick={() =>
                                setActivePositions(
                                  activePositions.filter(p => p !== pos)
                                )
                              }
                            >
                              Poista
                            </Button>
                          </td>
                          <td style={{ width: '130px' }}>
                            {startingProfiles[pos] && (
                              <Button
                                variant="link"
                                size="sm"
                                style={{
                                  color: '#17a2b8',
                                  padding: '0',
                                }}
                                onClick={() => copyAsNewPlayer(pos)}
                              >
                                Kopioi uudeksi
                              </Button>
                            )}
                          </td>
                          <td style={{ width: '100px' }}>
                            {/* Näytä "Vie suunnitelma" jos rivi on sidottu pelaajaan (myös custom-id:t) */}
                            {pos.includes('_') &&
                              /_\d{9}$/.test(pos) &&
                              fetchedData[pos] && (
                                <>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    style={{ color: '#28a745', padding: '0' }}
                                    onClick={() => handleExportPlan(pos)}
                                  >
                                    {t('exportPlan')}
                                  </Button>
                                  {Array.isArray(fetchedPlans[pos]) &&
                                    fetchedPlans[pos].length > 0 && (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        style={{ color: '#17a2b8', padding: '0', marginLeft: '8px' }}
                                        onClick={() => setSelectedTrainingPlan(pos)}
                                      >
                                        Näytä treenijärjestys
                                      </Button>
                                    )}
                                </>
                              )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
                <div style={{ marginTop: '12px' }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowWeeksTable(!showWeeksTable)}
                  >
                    {showWeeksTable
                      ? t('hideTrainingWeeks')
                      : t('showTrainingWeeks')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {showWeeksTable && (
          <Row className="mt-4">
            <Col>
              <Card
                style={{
                  backgroundColor: '#23272A',
                  borderColor: '#1E2124',
                }}
              >
                <Card.Body>
                  <Card.Title>Harjoitusviikot</Card.Title>
                  <Table striped bordered hover variant="dark" size="sm">
                    <thead>
                      <tr>
                        {[...positionTable.header.slice(0, 8), 'Treenijärjestys'].map(col => (
                          <th key={`weeks-${col}`}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activePositions.map((pos, posIdx) => {
                        // Käytä positionLabels labelia jos olemassa, muuten getPositionDisplayLabel
                        const label = getPositionDisplayLabel(
                          pos,
                          positionLabels
                        )
                        const weeksData =
                          fetchedWeeks[pos] || Array(7).fill('-')
                        return (
                          <tr key={`weeks-${posIdx}`}>
                            <td>{label}</td>
                            {weeksData.map((val, idx) => (
                              <td key={`weeks-skill-${posIdx}-${idx}`}>
                                {typeof val === 'number' ? val : val}
                              </td>
                            ))}
                            <td>
                              {Array.isArray(fetchedPlans[pos]) &&
                                fetchedPlans[pos].length > 0 && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    style={{ color: '#17a2b8', padding: '0' }}
                                    onClick={() => setSelectedTrainingPlan(pos)}
                                  >
                                    Näytä treenijärjestys
                                  </Button>
                                )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        <Modal
          show={selectedTrainingPlan !== null}
          onHide={() => setSelectedTrainingPlan(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Harjoittelujärjestys</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Harjoitusviikot</th>
                  <th>Harjoitusmuoto</th>
                  <th>Taitotaso</th>
                  <th>Ikä</th>
                </tr>
              </thead>
              <tbody>
                {groupTrainingPlan(
                  selectedTrainingPlan ? fetchedPlans[selectedTrainingPlan] : []
                ).map((entry, index) => (
                  <tr key={`${entry.skill}-${index}`}>
                    <td>{entry.weeks}</td>
                    <td>{trainingModeLabels[entry.skill] || entry.skill}</td>
                    <td>
                      {Number.isFinite(entry.skillAfter)
                        ? entry.skillAfter.toFixed(2)
                        : '-'}
                    </td>
                    <td>{formatAge(entry.ageAfter)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Modal.Body>
        </Modal>
        <Row className="mt-4">
          <Col>
            <Card
              style={{
                backgroundColor: '#23272A',
                borderColor: '#1E2124',
              }}
            >
              <Card.Body>
                <Card.Title>{t('startingSkillsAndAge')}</Card.Title>
                {/* Piilotetaan pelaajan haku ID:llä kun ollaan optimointi-moodissa */}
                {!isOptimizeMode && (
                  <div className="mb-3">
                    <Row className="g-2">
                      <Col md={3}>
                        <Form.Label>{t('searchPlayerById')}</Form.Label>
                      </Col>
                      <Col md={3}>
                        <Form.Control
                          type="text"
                          placeholder={t('playerId')}
                          value={playerIdInput}
                          onChange={e => setPlayerIdInput(e.target.value)}
                          style={{
                            backgroundColor: '#23272A',
                            color: '#FFFFFF',
                            border: '1px solid #1E2124',
                          }}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Select
                          value={selectedPosForFetch}
                          onChange={e => setSelectedPosForFetch(e.target.value)}
                          style={{
                            backgroundColor: '#23272A',
                            color: '#FFFFFF',
                            border: '1px solid #1E2124',
                          }}
                        >
                          {activePositions.map((pos, idx) => {
                            const label = getSelectionLabel(pos)
                            return (
                              <option key={`fetch-pos-${idx}`} value={pos}>
                                {label}
                              </option>
                            )
                          })}
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Button variant="primary" onClick={fetchPlayerById}>
                          {t('search')}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
                <Table striped bordered hover variant="dark" size="sm">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '80px', maxWidth: '120px' }}>
                        Pos
                      </th>
                      <th style={{ width: '100px' }}>GK</th>
                      <th style={{ width: '100px' }}>D</th>
                      <th style={{ width: '100px' }}>PM</th>
                      <th style={{ width: '100px' }}>W</th>
                      <th style={{ width: '100px' }}>PS</th>
                      <th style={{ width: '100px' }}>SC</th>
                      <th style={{ width: '100px' }}>SP</th>
                      <th style={{ width: '100px' }}>Age (y.d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activePositions.map((pos, posIdx) => {
                      // Use custom label if it exists, otherwise format the position.
                      const label = getPositionDisplayLabel(pos, positionLabels)
                      const prof = startingProfiles[pos] || {
                        skills: getDefaultStartForPosition(pos),
                        ageStr: '17.0',
                      }
                      const skills = prof.skills
                      return (
                        <tr key={`start-${posIdx}`}>
                          <td style={{ minWidth: '80px', maxWidth: '120px' }}>
                            {label}
                          </td>
                          {['GK', 'D', 'PM', 'W', 'PS', 'SC', 'SP'].map(k => (
                            <td
                              key={`start-${posIdx}-${k}`}
                              style={{ padding: 0 }}
                            >
                              <input
                                type="text"
                                inputMode="decimal"
                                value={String(skills[k] ?? '').replace(',', '.')}
                                onChange={e => {
                                  const val = e.target.value.replace(',', '.')
                                  setStartingProfiles(prev => ({
                                    ...prev,
                                    [pos]: {
                                      ...prev[pos],
                                      skills: { ...skills, [k]: val },
                                      ageStr: prof.ageStr,
                                    },
                                  }))
                                }}
                                style={{
                                  width: '100%',
                                  padding: '4px',
                                  backgroundColor: '#23272A',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  textAlign: 'center',
                                }}
                              />
                            </td>
                          ))}
                          <td style={{ padding: 0 }}>
                            <input
                              type="text"
                              value={String(prof.ageStr ?? '').replace(',', '.')}
                              onChange={e => {
                                const val = e.target.value.replace(',', '.')
                                setStartingProfiles(prev => ({
                                  ...prev,
                                  [pos]: {
                                    ...prev[pos],
                                    skills: { ...skills },
                                    ageStr: val,
                                  },
                                }))
                              }}
                              placeholder="17.0"
                              style={{
                                width: '100%',
                                padding: '4px',
                                backgroundColor: '#23272A',
                                color: '#FFFFFF',
                                border: 'none',
                                textAlign: 'center',
                              }}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <Card
              style={{ backgroundColor: '#23272A', borderColor: '#1E2124' }}
            >
              <Card.Body>
                <Card.Title>Harjoitusparametrit ja skill cap</Card.Title>
                <div className="mb-3">
                  <Table striped bordered hover variant="dark" size="sm">
                    <thead>
                      <tr>
                        <th>Coach</th>
                        <th>Assistants</th>
                        <th>Intensity</th>
                        <th>Stamina</th>
                        <th>Minutes</th>
                        <th>Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          'coach',
                          'assistants',
                          'intensity',
                          'stamina',
                          'minutes',
                          'efficiency',
                        ].map(key => (
                          <td key={`tp-${key}`} style={{ padding: 0 }}>
                            <input
                              type="text"
                              value={
                                trainingParams[key] !== undefined &&
                                trainingParams[key] !== null
                                  ? trainingParams[key]
                                  : ''
                              }
                              onChange={e =>
                                setTrainingParams(prev => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              style={{
                                width: '100%',
                                padding: '4px',
                                backgroundColor: '#23272A',
                                color: '#FFFFFF',
                                border: 'none',
                                textAlign: 'center',
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div>
                  <Table striped bordered hover variant="dark" size="sm">
                    <thead>
                      <tr>
                        {[
                          'keeperSkill',
                          'defenderSkill',
                          'playmakerSkill',
                          'wingerSkill',
                          'passingSkill',
                          'scorerSkill',
                          'setPiecesSkill',
                        ].map(h => (
                          <th key={`cap-h-${h}`}>{h.replace('Skill', '')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          'keeperSkill',
                          'defenderSkill',
                          'playmakerSkill',
                          'wingerSkill',
                          'passingSkill',
                          'scorerSkill',
                          'setPiecesSkill',
                        ].map(key => (
                          <td key={`cap-${key}`} style={{ padding: 0 }}>
                            <input
                              type="text"
                              value={
                                caps[key] !== undefined && caps[key] !== null
                                  ? caps[key]
                                  : ''
                              }
                              onChange={e =>
                                setCaps(prev => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              style={{
                                width: '100%',
                                padding: '4px',
                                backgroundColor: '#23272A',
                                color: '#FFFFFF',
                                border: 'none',
                                textAlign: 'center',
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <Card
              style={{
                backgroundColor: '#23272A',
                borderColor: '#1E2124',
              }}
            >
              <Card.Body>
                <Card.Title>Position Distributions</Card.Title>
                <div>Multipliers</div>
                <div className="mb-3">
                  <Table striped bordered hover variant="dark" size="sm">
                    <thead>
                      <tr>
                        {[
                          'keeperSkill',
                          'defenderSkill',
                          'playmakerSkill',
                          'wingerSkill',
                          'passingSkill',
                          'scorerSkill',
                          'setPiecesSkill',
                        ].map(h => (
                          <th key={h}>{h.replace('Skill', '')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          'keeperSkill',
                          'defenderSkill',
                          'playmakerSkill',
                          'wingerSkill',
                          'passingSkill',
                          'scorerSkill',
                          'setPiecesSkill',
                        ].map(key => (
                          <td key={`mp-${key}`} style={{ padding: 0 }}>
                            <input
                              type="text"
                              value={
                                multipliers[key] !== undefined &&
                                multipliers[key] !== null
                                  ? multipliers[key]
                                  : ''
                              }
                              onChange={e =>
                                setMultipliers(prev => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              placeholder="1.0"
                              style={{
                                width: '100%',
                                padding: '4px',
                                backgroundColor: '#23272A',
                                color: '#FFFFFF',
                                border: 'none',
                                textAlign: 'center',
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div>
                  {/* Pareita näytetään kaksi rinnakkain */}
                  {Array.from(
                    { length: Math.ceil(activePositions.length / 2) },
                    (_, i) => activePositions.slice(i * 2, i * 2 + 2)
                  ).map((pair, pairIdx) => (
                    <Row key={pairIdx} className="g-3 mb-4">
                      {pair.map((pos, posInPairIdx) => {
                        // Extract base position for lookup (e.g., "RWBN_123456789" -> "RWBN")
                        const basePos = pos.split('_')[0]
                        // Käytä customDistributions jos saatavilla, muuten positionDistributions
                        const dist =
                          customDistributions[pos] ||
                          positionDistributions[
                            basePos === 'GKD' ? 'GK' : basePos
                          ]
                        if (!dist) return null

                        // Käytä positionLabels jos olemassa
                        const label = getPositionDisplayLabel(
                          pos,
                          positionLabels
                        )

                        // Keräät kaikki bucketit ja taidot
                        const buckets = Object.keys(dist)
                        const allSkills = [
                          ...new Set(
                            Object.values(dist).flatMap(bucket =>
                              Object.keys(bucket)
                            )
                          ),
                        ]

                        // Helper: tiivistä bucket-nimet
                        const shortenBucket = bucket => {
                          const match = bucket.match(/[A-Z]/g)
                          return match ? match.join('') : bucket
                        }

                        // Helper: poista "Skill" sanan lopusta
                        const shortenSkill = skill => {
                          return skill.replace('Skill', '')
                        }

                        return (
                          <Col md={6} key={`dist-${pairIdx}-${posInPairIdx}`}>
                            <div>
                              <h6>{label}</h6>
                              <Table
                                striped
                                bordered
                                hover
                                variant="dark"
                                size="sm"
                              >
                                <thead>
                                  <tr>
                                    <th></th>
                                    {buckets.map(bucket => (
                                      <th key={bucket}>
                                        {shortenBucket(bucket)}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {allSkills.map(skill => (
                                    <tr key={skill}>
                                      <td>{shortenSkill(skill)}</td>
                                      {buckets.map(bucket => {
                                        const value =
                                          customDistributions[pos] &&
                                          customDistributions[pos][bucket] &&
                                          customDistributions[pos][bucket][
                                            skill
                                          ]
                                        return (
                                          <td
                                            key={`${bucket}-${skill}`}
                                            style={{ padding: '0' }}
                                          >
                                            <input
                                              type="text"
                                              value={formatPercentDisplay(
                                                value
                                              )}
                                              onChange={e => {
                                                const newDist = JSON.parse(
                                                  JSON.stringify(
                                                    customDistributions
                                                  )
                                                )
                                                if (!newDist[pos])
                                                  newDist[pos] = JSON.parse(
                                                    JSON.stringify(
                                                      positionDistributions[pos] ||
                                                        positionDistributions[
                                                          pos === 'GKD'
                                                            ? 'GK'
                                                            : pos
                                                        ]
                                                    )
                                                  )
                                                if (!newDist[pos][bucket])
                                                  newDist[pos][bucket] = {}
                                                const decimal =
                                                  parsePercentToDecimal(
                                                    e.target.value
                                                  )
                                                newDist[pos][bucket][skill] =
                                                  decimal
                                                setCustomDistributions(newDist)
                                              }}
                                              style={{
                                                width: '100%',
                                                padding: '4px',
                                                backgroundColor: '#23272A',
                                                color: '#FFFFFF',
                                                border: 'none',
                                                textAlign: 'center',
                                              }}
                                            />
                                          </td>
                                        )
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                        )
                      })}
                    </Row>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default SkillDistribution
